import type { SiteRagReport } from '../../../types/siteRag';
import { buildReportArtifact, enqueueArtifactJob } from '../../../services/reportArtifactJobsClient';
import { createReportArtifactRequest } from '../../../lib/reportArtifacts/reportArtifactRequest';
import { reportSummaryEligibility, reportSummaryViewForType, reportSummaryBuilderVersionForView } from '../../../lib/reportSummary/eligibility';
import { useStore } from '../../../store';
import { resolveScreeningSummaryPageSize, type SummaryDownloadOptions } from './summaryArtifactActions';
import { buildReportExportFilename } from '../../../lib/reportArtifacts/reportExportFilename';

const prebuilt = new Set<string>();

export function buildReportSummaryCacheKey(
  report: SiteRagReport,
  pageSize?: 'A4' | 'Letter',
  companyId?: string | null,
): string {
  const resolvedCompanyId =
    companyId !== undefined ? (companyId ?? '') : (useStore.getState()?.companyId ?? '');
  const ownerUid = resolveSummaryReportOwnerUid(report) ?? '';
  const siteId = report.siteId ?? '';
  const revId = report.reportRevision?.revisionId ?? 'norev';
  const docHash = report.reportRevision?.documentHash ?? 'nohash';
  const reportType = report.reportType ?? 'feasibility';
  const view = reportSummaryViewForType(reportType);
  const summaryBuilderVersion = reportSummaryBuilderVersionForView(view);
  return `${resolvedCompanyId}:${ownerUid}:${siteId}:${report.id}:${revId}:${docHash}:${view}:${summaryBuilderVersion}:${pageSize ?? 'frozen'}`;
}

export function clearReportSummaryCache(): void {
  prebuilt.clear();
}

export function getReportSummaryCacheKeys(): string[] {
  return Array.from(prebuilt.values());
}

function resolveSummaryReportOwnerUid(report: SiteRagReport): string | undefined {
  const explicit = (report as { reportOwnerUid?: unknown }).reportOwnerUid;
  if (typeof explicit === 'string' && explicit.trim()) return explicit.trim();
  if (typeof report._ownerUid === 'string' && report._ownerUid.trim()) return report._ownerUid.trim();
  const rev = report.reportRevision;
  const pathCandidate = rev?.sourcePath ?? (rev as { bodyPath?: string } | undefined)?.bodyPath ?? rev?.presentationPath ?? rev?.documentPath ?? '';
  const pathMatch = /^users\/([^/]+)\/reports\//.exec(pathCandidate);
  if (pathMatch?.[1]?.trim()) return pathMatch[1].trim();
  return undefined;
}
async function completeRevision(report: SiteRagReport, expectedCompanyId?: string | null): Promise<SiteRagReport> {
  if (!report._metadataOnly) return report;
  const ok = await useStore.getState().hydrateReportBody(report.id);
  if (expectedCompanyId !== undefined && (useStore.getState()?.companyId ?? null) !== expectedCompanyId) {
    throw new Error('The active tenant changed while loading the saved revision.');
  }
  const body = useStore.getState().siteRag.reports.find(candidate => candidate.id === report.id);
  if (!ok || !body || body._metadataOnly || body.reportRevision?.revisionId !== report.reportRevision?.revisionId) {
    throw new Error('The saved revision changed while loading. Open the complete report before creating its summary.');
  }
  return body;
}

/** Normal artifact identity/currentness guards apply to every new summary. */
export function prebuildReportSummary(
  report: SiteRagReport,
  pageSize?: 'A4' | 'Letter',
  companyId?: string | null,
): void {
  if (import.meta.env.DEV && import.meta.env.VITE_SCREENING_SUMMARY_PREBUILD === 'false') return;
  if (!reportSummaryEligibility(report).ok || !report.reportRevision) return;
  const reportType = report.reportType ?? 'feasibility';
  const view = reportSummaryViewForType(reportType);
  const summaryBuilderVersion = reportSummaryBuilderVersionForView(view);
  const expectedCompanyId =
    companyId !== undefined ? companyId : (useStore.getState()?.companyId ?? null);
  const key = buildReportSummaryCacheKey(report, pageSize, expectedCompanyId);
  if (prebuilt.has(key)) return;
  prebuilt.add(key);
  void (async () => {
    const source = await completeRevision(report, expectedCompanyId);
    const paper = pageSize ?? await resolveScreeningSummaryPageSize(source);
    if ((useStore.getState()?.companyId ?? null) !== expectedCompanyId) {
      prebuilt.delete(key);
      return;
    }
    const ownerUid = resolveSummaryReportOwnerUid(source);
    const request = createReportArtifactRequest(source, 'pdf', paper, { view, summaryBuilderVersion });
    await (ownerUid
      ? enqueueArtifactJob(request, undefined, undefined, `?reportOwnerUid=${encodeURIComponent(ownerUid)}`)
      : enqueueArtifactJob(request));
  })().catch(() => { prebuilt.delete(key); });
}

export async function downloadReportSummary(
  report: SiteRagReport,
  pageSizeOrOptions?: 'A4' | 'Letter' | SummaryDownloadOptions,
): Promise<void> {
  const options: SummaryDownloadOptions =
    typeof pageSizeOrOptions === 'string'
      ? { pageSize: pageSizeOrOptions }
      : (pageSizeOrOptions ?? {});
  const expectedCompanyId =
    options.companyId !== undefined ? options.companyId : (useStore.getState()?.companyId ?? null);
  const source = await completeRevision(report, expectedCompanyId);
  if ((useStore.getState()?.companyId ?? null) !== expectedCompanyId) return;
  if (options.shouldSave && !options.shouldSave()) return;
  const eligibility = reportSummaryEligibility(source);
  if (!eligibility.ok) throw new Error(eligibility.reason);
  const reportType = source.reportType ?? 'feasibility';
  const view = reportSummaryViewForType(reportType);
  const paper = options.pageSize ?? await resolveScreeningSummaryPageSize(source);
  if ((useStore.getState()?.companyId ?? null) !== expectedCompanyId) return;
  if (options.shouldSave && !options.shouldSave()) return;
  const request = createReportArtifactRequest(source, 'pdf', paper, {
    view, summaryBuilderVersion: reportSummaryBuilderVersionForView(view),
  });
  const ownerUid = resolveSummaryReportOwnerUid(source);
  const expectedFilename = buildReportExportFilename({
    siteName: source.siteName,
    reportType,
    view: 'report-summary',
    version: source.version ?? 1,
    minorVersion: source.minorVersion ?? 0,
    format: 'pdf',
  });
  const built = ownerUid ? await buildReportArtifact(request, { reportOwnerUid: ownerUid }) : await buildReportArtifact(request);
  if ((useStore.getState()?.companyId ?? null) !== expectedCompanyId) return;
  if (options.shouldSave && !options.shouldSave()) return;
  if (options.save) {
    options.save(built.blob, expectedFilename);
    return;
  }
  const { saveAs } = await import('file-saver');
  if ((useStore.getState()?.companyId ?? null) !== expectedCompanyId) return;
  if (options.shouldSave && !options.shouldSave()) return;
  saveAs(built.blob, expectedFilename);
}
